import { CommonModule } from '@angular/common';
import { Component, inject, Input } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import _ from 'lodash';
import * as uuid from "uuid";
import { RestService } from '../../../core/services/rest.service';
import { CMDRestWebHeaderTableColumns, CMDRestWebParamTableColumns } from '../../../core/table-templates/cmd-rest';
import { ZorroTableComponent } from '../../common/custom-table/zorro-table.component';

@Component({
  selector: 'app-cmd-rest-web-service-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ZorroTableComponent],
  templateUrl: './cmd-rest-web-service-modal.component.html',
  styleUrl: './cmd-rest-web-service-modal.component.scss'
})
export class CmdRestWebServiceModalComponent {
  activeModal = inject(NgbActiveModal);

  @Input()
  title: string = 'Call Rest Web Services';

  @Input()
  id: string = '';

  @Input()
  fields: any = [];

  @Input()
  services: any = [];

  @Input()
  operations: any = [];

  @Input()
  isEdit = false;

  @Input()
  routeParams: any = {};

  @Input()
  variables: any = [];

  @Input()
  restApiDraftVariables: any = {};

  @Input() intellisense: any = [];

  // class/component's variables
  data: any = {
    isReportFailure: true,
    service: '',
    operation: ''
  };
  paramTable: any = {
    columns: CMDRestWebParamTableColumns,
    rows: []
  };
  headerTable: any = {
    columns: CMDRestWebHeaderTableColumns,
    rows: []
  };
  clonedData = {
    param: [],
    header: []
  };

  isOperationLoading = false;
  operationLoaderText = '';
  variableList: any = [];
  autocompleteOptionsByKey: any = {};
  isActive:boolean = false;
  constructor(private readonly rest: RestService) {}

  ngOnInit() {
    if(this.intellisense) {
      this.autocompleteOptionsByKey['value'] = this.intellisense;
    }
    this.variableList = _.cloneDeep(this.variables);
    if(this.isEdit) {
      this.fields.forEach((field: any) => {
        switch (field.id) {
          case 'param_service':
            this.data.service = field.value;
            break;
          case 'param_operation':
            this.data.operation = field.value;
            break;
          case "param_parameters":
            this.paramTable.rows = field.value.map((item: any) => ({
              ...item,
              value: item.name === "Payload" ? JSON.stringify(item.value) : item.value
            }));            
            break;
          case 'param_headers':
            this.headerTable.rows = [...field.value];
            break;
          case 'param_report_failures':
            this.data.isReportFailure = field.value;
            break;
          case 'param_variable_name':
            if(field.value && !this.variableList.find((ele: any) => ele.name === field.value)) {
              this.restApiDraftVariables[this.id] = field.value;
            }
            if(this.restApiDraftVariables[this.id]) this.variableList.push({ name: this.restApiDraftVariables[this.id] });
            this.data.variable = field.value;
            break;
        }
      });
      this.selectService(true);
    }
  }

  handleParamTableActions(event: any) {
    let isFieldsFilled = event?.data?.row?.name && event?.data?.row?.value;
    switch (event.actionType) {
      case 'add':
        if (!isFieldsFilled) {
          return;
        }
        if (isFieldsFilled && event?.data?.row?.isDraft) {
          const obj = {
            ...this.paramTable.rows[0],
            isRowEditable: false,
            isDraft: false,
            id: uuid.v4()
          };
          this.paramTable.rows.shift();
          this.paramTable.rows = [obj, ...this.paramTable.rows];
          this.clonedData['param'] = _.cloneDeep(this.paramTable.rows);
        } else {
          this.paramTable.rows = this.paramTable.rows.map((row: any) => {
            if (row.id === event.data.row.id) {
              return {
                ...row,
                isDraft: false,
                isRowEditable: false
              };
            }
            return row;
          });
          this.clonedData['param'] = _.cloneDeep(this.paramTable.rows);
        }
        break;
      case 'edit':
        if (event?.data?.row?.id) {
          this.paramTable.rows = this.paramTable.rows.map((row: any) => {
            if (row.id === event.data.row.id) {
              return {
                ...row,
                isRowEditable: true
              };
            }
            return row;
          });
        }
        break;
      case "cancel":
        if (event?.data?.row?.isRowEditable) {
          this.paramTable.rows = this.paramTable.rows.map((row: any) => {
            if (row.id === event.data.row.id) {
              return {
                ...row,
                isDraft: false,
                isRowEditable: false,
              };
            }
            return row;
          });
          this.clonedData["param"] = _.cloneDeep(this.paramTable.rows);
          // this.paramTable.rows.shift();
          // this.paramTable.rows = [...this.paramTable.rows];
        } else {
          let objIndx = this.paramTable.rows.findIndex((row: any) => row.id === event.data.row.id);
          this.paramTable.rows[objIndx] = this.clonedData['param'].find((ele: any) => ele.id === event.data.row.id);
          this.paramTable.rows = [...this.paramTable.rows];
        }
        break;
      case 'delete':
        if (event?.data?.row?.id) {
          let ind = this.paramTable.rows.findIndex((ele: any) => ele.id === event.data.row.id);
          this.paramTable.rows.splice(ind, 1);
          this.paramTable.rows = [...this.paramTable.rows];
          this.clonedData["param"] = _.cloneDeep(this.paramTable.rows);
        }
        break;
    }
  }

  safeParse(value: string): any {
    try {
      return JSON.parse(value);
    } catch {
      return value;
    }
  }

  handleHeaderTableActions(event: any) {
    let isFieldsFilled = event?.data?.row?.key && event?.data?.row?.value;
    switch (event.actionType) {
      case 'add':
        if (!isFieldsFilled) {
          return;
        }
        if (event?.data?.row?.isDraft) {
          const obj = {
            ...this.headerTable.rows[0],
            isRowEditable: false,
            isDraft: false,
            id: uuid.v4()
          };
          this.headerTable.rows.shift();
          this.headerTable.rows = [obj, ...this.headerTable.rows];
          this.clonedData['header'] = _.cloneDeep(this.headerTable.rows);
        } else {
          this.headerTable.rows = this.headerTable.rows.map((row: any) => {
            if (row.id === event.data.row.id) {
              return {
                ...row,
                isDraft: false,
                isRowEditable: false
              };
            }
            return row;
          });
          this.clonedData['header'] = _.cloneDeep(this.headerTable.rows);
        }
        break;
      case 'edit':
        if (event?.data?.row?.id) {
          this.headerTable.rows = this.headerTable.rows.map((row: any) => {
            if (row.id === event.data.row.id) {
              return {
                ...row,
                isRowEditable: true
              };
            }
            return row;
          });
        }
        break;
      case 'cancel':
        if (event?.data?.row?.isDraft) {
          this.headerTable.rows.shift();
          this.headerTable.rows = [...this.headerTable.rows];
        } else {
          let objIndx = this.headerTable.rows.findIndex((row: any) => row.id === event.data.row.id);
          this.headerTable.rows[objIndx] = this.clonedData['header'].find((ele: any) => ele.id === event.data.row.id);
          this.headerTable.rows = [...this.headerTable.rows];
        }
        break;
      case 'delete':
        if (event?.data?.row?.id) {
          let ind = this.headerTable.rows.findIndex((ele: any) => ele.id === event.data.row.id);
          this.headerTable.rows.splice(ind, 1);
          this.headerTable.rows = [...this.headerTable.rows];
          this.clonedData['header'] = _.cloneDeep(this.headerTable.rows);
        }
        break;

    }
  }

  addParam() {
    const obj = {
      name: '',
      value: '',
      isRowEditable: true,
      isDraft: true
    };
    const existingRows = Array.isArray(this.paramTable.rows) ? this.paramTable.rows : [];
    this.paramTable.rows = [obj, ...existingRows];
  }

  addHeader() {
    const obj = {
      key: '',
      value: '',
      isRowEditable: true,
      isDraft: true
    };
    this.headerTable.rows = [obj, ...this.headerTable.rows];
  }

  addCommand() {
    const result: any = {};
    this.fields.forEach((field: any) => {
      switch (field.id) {
        case 'param_service':
          field.value = this.data.service;
          field.displayValue = this.services.find((ele: any) => ele.id === this.data.service)?.label;
          break;
        case 'param_operation':
          field.value = this.data.operation;
          field.displayValue = this.operations.find((ele: any) => ele.id === this.data.operation)?.label;
          break;
        case "param_parameters":
          field.value = this.paramTable?.rows
            ? this.paramTable?.rows?.map((item: any) => {
                if (item?.name.toLowerCase() === "payload" && item?.value) {
                  let dataObj = {
                    name: item.name,
                    value: this.safeParse(item?.value),
                  };
                  return dataObj;
                } else {
                  return {
                    name: item.name,
                    value: item.value,
                  };
                }
              })
            : [];
          break;
        case 'param_headers':
          const headerRows = Array.isArray(this.headerTable?.rows) ? this.headerTable.rows : [];
          field.value = headerRows.map((item: any) => ({
            key: item.key,
            value: item.value
          }));
          break;
        case 'param_report_failures':
          field.value = this.data?.isReportFailure;
          break;
        case 'param_variable_name':
          field.value = this.data?.variable;
          break;
      }
    });
    this.activeModal.close({ fields: this.fields });
  }

  selectService(isInitial?: boolean) {
    this.operations = [];
    if(!isInitial) this.data.operation = '';
    this.isOperationLoading = true;
    this.operationLoaderText = '';
    if(this.services.find((ele: any) => ele.value === this.data.service)?.isDraft) {
      this.isOperationLoading = false;
      let opr = this.fields.find((item: any) => item.id === 'param_operation');
      this.operations.push(
        {
          label: opr?.displayValue,
          value: opr?.value
        }
      );
      return;
    }
    this.rest.getAllServiceOperations(this.routeParams.projectId, this.routeParams.automationId, this.data.service).subscribe((res: any) => {
      this.isOperationLoading = false;
      if(!res.data?.length) this.operationLoaderText = 'No operations found for the selected service';
      this.operations = res?.data?.map((ele: any) => {
        return {
          ...ele,
          label: ele.name,
          value: ele.id
        }
      }, () => {
        this.isOperationLoading = false;
        this.operationLoaderText = 'Failed to load operations';
      });
    });
  }

  onOperationChange(selectedValue: any): void {
    let operation = this.operations.find((item:any) => item.id === selectedValue);
    this.paramTable.rows = operation?.parameters?.length ? operation?.parameters : [];
    this.headerTable.rows = operation?.headers?.length ? operation.headers : [];
  }
  
}
